import MuiTextField, { OutlinedTextFieldProps } from '@mui/material/TextField'
import { isValidFileType } from '@app/src/lib/file'
import React from 'react'
import { useI18n } from '@app/configuration/contexts/I18nContext'

import { FormErrors } from '@app/src/hooks/useForm'

interface TextFieldProps
  extends Omit<OutlinedTextFieldProps, 'variant' | 'onInput'> {
  clientErrorText?: string
  id: OutlinedTextFieldProps['id'] // Marked as required for accessibility
  // Redefine `onInput`instead of type HTMLDivElement
  onInput?: React.FormEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onValidFile?: ({
    blob,
    imageHeight,
    imageWidth,
    objectURL,
  }: {
    blob: Blob
    imageHeight: number
    imageWidth: number
    objectURL: string
  }) => void
  inputProps?: Omit<OutlinedTextFieldProps['inputProps'], 'onInput'>
  serverErrors?: FormErrors | null
}

const TextField: React.FC<TextFieldProps> = ({
  clientErrorText,
  InputLabelProps,
  InputProps,
  inputProps,
  name,
  onInput,
  onValidFile,
  serverErrors,
  ...restOfProps
}) => {
  const errorText = clientErrorText ?? serverErrors?.[name || '']
  const i18n = useI18n()

  const onPaste = React.useCallback(
    (e: any) => {
      if (e.clipboardData.files.length > 0 && onValidFile) {
        const file = e.clipboardData.files[0]

        isValidFileType(file)
          .then(({ blob, imageHeight, imageWidth, objectURL }) => {
            ;(blob as any).name ||= file.name

            onValidFile({
              blob,
              imageHeight,
              imageWidth,
              objectURL,
            })
          })
          .catch(() => {
            window.alert(i18n.t('fileBrowser.unsupportedFormat'))
          })
      }
    },
    [i18n, onValidFile],
  )

  return (
    <MuiTextField
      error={Boolean(errorText)}
      fullWidth
      helperText={errorText}
      name={name}
      {...restOfProps}
      variant="outlined"
      InputLabelProps={{ shrink: true, ...InputLabelProps }}
      InputProps={{ notched: false, ...InputProps }}
      inputProps={{
        ...inputProps,
        onInput,
        onPaste,
      }}
    />
  )
}

export default TextField
